import type {
  CastingProfileResponse,
  UpdateCastingProfileRequest
} from '@innovationdepartment/proxima-sdk-axios';
import { useCallback, useMemo, useState } from 'react';

import type { CastingProfileFormErrors, CastingProfileFormState } from './CastingProfile.types';

const emptyFormState = (): CastingProfileFormState => ({
  vibeSummary: '',
  onBrandAttractiveness: [],
  onBrandBuilds: [],
  targetModelAgeMin: null,
  targetModelAgeMax: null
});

const fromProfile = (profile: CastingProfileResponse | null): CastingProfileFormState => {
  if (!profile) return emptyFormState();

  return {
    vibeSummary: profile.vibeSummary,
    onBrandAttractiveness: [...profile.onBrandAttractiveness],
    onBrandBuilds: [...profile.onBrandBuilds],
    targetModelAgeMin: profile.targetModelAgeMin,
    targetModelAgeMax: profile.targetModelAgeMax
  };
};

// Mirrors the server-side validation on PUT /brand/:brandId/avatars/casting-profile
const validate = (state: CastingProfileFormState): CastingProfileFormErrors => {
  const errors: CastingProfileFormErrors = {};

  if (!state.vibeSummary.trim()) errors.vibeSummary = 'Vibe summary is required';

  if (!state.onBrandAttractiveness.length) {
    errors.onBrandAttractiveness = 'Select at least one attractiveness level';
  }

  if (!state.onBrandBuilds.length) errors.onBrandBuilds = 'Select at least one build type';

  const { targetModelAgeMin: ageMin, targetModelAgeMax: ageMax } = state;
  if (ageMin == null) errors.targetModelAgeMin = 'Enter a whole number of 0 or more';
  if (ageMax == null) {
    errors.targetModelAgeMax = 'Enter a whole number of 0 or more';
  } else if (ageMin != null && ageMin > ageMax) {
    errors.targetModelAgeMax = 'Max age must be greater than or equal to min age';
  }

  return errors;
};

function useCastingProfileForm(initialProfile: CastingProfileResponse | null) {
  const [formState, setFormState] = useState<CastingProfileFormState>(() =>
    fromProfile(initialProfile)
  );
  const [savedState, setSavedState] = useState<CastingProfileFormState>(formState);

  const setField = useCallback(
    <K extends keyof CastingProfileFormState>(field: K, value: CastingProfileFormState[K]) => {
      setFormState(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  const reset = useCallback((profile: CastingProfileResponse | null) => {
    const next = fromProfile(profile);
    setFormState(next);
    setSavedState(next);
  }, []);

  const errors = useMemo(() => validate(formState), [formState]);
  const isValid = Object.keys(errors).length === 0;
  // Reordering an array field (e.g. reselecting the same builds) counts as dirty
  // even though the selection is set-equal. We accept that false positive.
  const isDirty = useMemo(
    () => JSON.stringify(formState) !== JSON.stringify(savedState),
    [formState, savedState]
  );

  const toRequest = useCallback(
    (): UpdateCastingProfileRequest => ({
      vibeSummary: formState.vibeSummary.trim(),
      onBrandAttractiveness: formState.onBrandAttractiveness,
      onBrandBuilds: formState.onBrandBuilds,
      targetModelAgeMin: formState.targetModelAgeMin ?? 0,
      targetModelAgeMax: formState.targetModelAgeMax ?? 0
    }),
    [formState]
  );

  return { formState, setField, errors, isValid, isDirty, reset, toRequest };
}

export default useCastingProfileForm;
